import React, { useMemo } from 'react';
import { useTheme } from '@mui/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryStack,
  VictoryTooltip
} from 'victory';
import {
  Paper,
  Grid,
  Typography,
  Tooltip,
  Button,
  Checkbox,
  FormControlLabel,
  ClassNameMap
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import SquareIcon from '@mui/icons-material/Square';
import { useResizeDetector } from 'react-resize-detector';
import { DateTime } from 'luxon';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { NoteInsightsViewOptions } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { domainPaddingType, useChartStyles } from './hooks/useChartStyles';
import { dataType } from './utils';
import MyUtilizationChartSkeleton from './MyUtilizationChartSkeleton';
import MyUtilizationChartNoData from './MyUtilizationChartNoData';
import { useMyUtilizationDataPagination } from './hooks/useMyUtilizationDataPagination';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  heading: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: theme.spacing(2.6)
  },
  legendText: {
    color: theme.palette.text.secondary
  },
  legendIcon: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    marginRight: theme.spacing(0.4)
  },
  legendBox: {
    paddingLeft: theme.spacing(2)
  },
  chartContainer: {
    paddingTop: theme.spacing(4)
  },
  paginator: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0)
  },
  paginationButton: {
    height: theme.spacing(0.5),
    textTransform: 'none',
    fontSize: 12
  },
  checkBox: {
    paddingLeft: theme.spacing(2.1),
    color: theme.palette.common.black
  },
  label: {
    '&.MuiFormControlLabel-root': {
      ...theme.typography.caption
    }
  }
}));

const MyCustomFlyout: React.FC<any> = ({
  x,
  y,
  name,
  datum
}: {
  x: number;
  y: number;
  name: string;
  datum: any;
}) => {
  const { formatMessage } = useIntl();

  const formattedTime = getFormattedDuration({
    formatMessage,
    duration: datum.totalDuration ?? 0,
    needsMask: true
  });
  return (
    <foreignObject x={x} y={y - 40} width="90" height="50">
      <Tooltip title={`${name}: ${formattedTime}`} open={true}>
        <span />
      </Tooltip>
    </foreignObject>
  );
};

type MyUtilizationChartProp = {
  tData: dataType[];
  checked: boolean;
  handleCheckBoxChange: (event: any) => void;
  loading: boolean;
  isEmpty: boolean;
  selectedViewOption: NoteInsightsViewOptions;
  overrideClass?: ClassNameMap;
};

const MyUtilizationChart: React.FC<MyUtilizationChartProp> = ({
  tData,
  checked,
  handleCheckBoxChange,
  loading,
  isEmpty,
  selectedViewOption,
  overrideClass
}: MyUtilizationChartProp) => {
  const theme = useTheme();

  const style = useChartStyles(theme);
  const classes = useStyles({ classes: overrideClass });

  const { width, ref: chartRef } = useResizeDetector();
  const {
    onNext,
    onPrevious,
    showNext,
    showPrev,
    currentData,
    dateSet
  } = useMyUtilizationDataPagination(tData, selectedViewOption);

  const barStyling = useMemo(() => {
    const noOfBars = currentData[0]?.data.length || 0;
    const widthContainer = (width || 0) - 100;
    let bWidth = 0;
    let dPadding = {};
    if (noOfBars <= 4) {
      {
        bWidth = ((widthContainer || 0) - 40 * 4) / 4;
        dPadding = { x: [85, 85], y: [0, 10] };
      }
    } else if (noOfBars <= 7) {
      bWidth = ((widthContainer || 0) - 13 * 7) / 7;
      dPadding = { x: [45, 45], y: [0, 10] };
    } else if (noOfBars <= 13) {
      bWidth = ((widthContainer || 0) - 9 * 13) / 13;
      dPadding = { x: [10, 10], y: [0, 10] };
    } else if (noOfBars <= 20) {
      {
        bWidth = ((widthContainer || 0) - 6 * 20) / 20;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    } else if (noOfBars <= 25) {
      {
        bWidth = ((widthContainer || 0) - 4.8 * 25) / 25;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    } else if (noOfBars <= 30) {
      {
        bWidth = ((widthContainer || 0) - 4 * 30) / 30;
        dPadding = { x: [0, 0], y: [0, 10] };
      }
    }
    return {
      barWidth: bWidth,
      domainPadding: dPadding as domainPaddingType
    };
  }, [currentData, width]);
  const tickValues = useMemo(() => dateSet.map((_z, index) => index + 1), [
    dateSet
  ]);

  const tickFormat = useMemo(
    () => dateSet.map((z) => DateTime.fromISO(z, { zone: 'utc' }).day),
    [dateSet]
  );

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'my-utilization-chart-container'}
      ref={chartRef}
    >
      {loading ? (
        <MyUtilizationChartSkeleton />
      ) : isEmpty ? (
        <MyUtilizationChartNoData />
      ) : (
        <Grid container>
          <Grid item container>
            <Grid
              item
              xs={5}
              container
              alignItems={'center'}
              justifyContent="space-between"
            >
              <Typography variant="h4" className={classes.heading}>
                <FormattedMessage id="insights.timeUtilizationChart.timeUtilizationChartHeading" />
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked
                    size="small"
                    className={classes.checkBox}
                    checked={checked}
                    onChange={handleCheckBoxChange}
                  />
                }
                disableTypography={true}
                label={
                  <Typography variant="caption" className={classes.legendText}>
                    <FormattedMessage id="insights.timeUtilizationChart.hideDaysLabel" />
                  </Typography>
                }
                className={classes.label}
              />
            </Grid>
            <Grid container item xs={7} justifyContent={'flex-end'}>
              {currentData.map((x) => (
                <Grid key={x.name} item className={classes.legendBox}>
                  <SquareIcon
                    fontSize="small"
                    sx={{ color: x.color }}
                    className={classes.legendIcon}
                  />
                  <Typography variant="caption" className={classes.legendText}>
                    {x.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid
            item
            container
            className={classes.chartContainer}
            data-testid={'my-utilization-chart'}
          >
            <VictoryChart
              domainPadding={barStyling.domainPadding}
              height={style.VictoryChart.height}
              width={width}
              padding={style.VictoryChart.padding}
              containerComponent={<VictoryContainer responsive={true} />}
            >
              <VictoryAxis
                tickValues={tickValues}
                tickFormat={tickFormat}
                style={style.VictoryAxis}
              />
              <VictoryAxis
                dependentAxis
                tickFormat={(x) => `${(x / 3600).toFixed(2)}h`}
                style={style.VictoryAxis}
              />
              {
                <VictoryStack domainPadding={{ x: 10 }}>
                  {currentData.map((x, _index) => {
                    return (
                      <VictoryBar
                        key={x.name}
                        barWidth={barStyling.barWidth}
                        data={x.data}
                        style={{
                          data: { fill: x.color }
                        }}
                        x="date"
                        y="totalDuration"
                        labels={() => ''}
                        labelComponent={
                          <VictoryTooltip
                            flyoutPadding={10}
                            flyoutComponent={<MyCustomFlyout name={x.name} />}
                          />
                        }
                        events={[
                          {
                            target: 'data',
                            eventHandlers: {
                              onMouseOver: () => {
                                return [
                                  {
                                    target: 'data',
                                    mutation: () => {
                                      return {
                                        style: {
                                          fill: currentData[_index].color,
                                          stroke:
                                            theme.palette.information
                                              .background,
                                          strokeWidth: 1,
                                          cursor: 'pointer'
                                        }
                                      };
                                    }
                                  },
                                  {
                                    target: 'labels',
                                    mutation: () => ({ active: true })
                                  }
                                ];
                              },
                              onMouseLeave: () => {
                                return [
                                  {
                                    target: 'data',
                                    mutation: () => {
                                      return {
                                        style: {
                                          fill: currentData[_index].color,
                                          strokeWidth: 0
                                        }
                                      };
                                    }
                                  },
                                  {
                                    target: 'labels',
                                    mutation: () => ({ active: undefined })
                                  }
                                ];
                              }
                            }
                          }
                        ]}
                      />
                    );
                  })}
                </VictoryStack>
              }
            </VictoryChart>
          </Grid>
          <Grid container className={classes.paginator}>
            {showPrev ? (
              <Button
                variant={'text'}
                data-testid="my-utilization-prev"
                className={classes.paginationButton}
                onClick={onPrevious}
              >
                <FormattedMessage id="insights.timeUtilizationChart.prev" />
              </Button>
            ) : (
              <span></span>
            )}
            {showNext ? (
              <Button
                variant={'text'}
                data-testid="my-utilization-next"
                className={classes.paginationButton}
                onClick={onNext}
              >
                <FormattedMessage id="insights.timeUtilizationChart.next" />
              </Button>
            ) : (
              <span></span>
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default React.memo(MyUtilizationChart);
